<script setup>
/**
 * FormDisplayOrderMenu
 */
import {ref, computed, onMounted, watch} from 'vue'
import {useFormStore} from '@/stores/form'
import Button from '@/common/Button.vue'
import api from '@/services/api'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const formStore = useFormStore(props.formId)

// --- State ---

const options = ref([])
const isLoading = ref(false)

// --- Computed ---

const currentData = computed(() => formStore.currentData)
const tableName = computed(() => formStore.tableName)
const id = computed(() => formStore.id)
const showDisplayOrderOption = computed(() => formStore.showDisplayOrderOption)

// --- Methods ---

/**
 * Handles the API call and updates the local 'options' ref.
 */
const fetchOptions = async () => {
    if (!tableName.value) return

    isLoading.value = true
    try {
        const response = await api.get('api/items-display-order-options', {
            params: {
                tableName: tableName.value,
                id: id.value
            }
        })
        options.value = response.data || []
    } catch (error) {
        console.error("Failed to fetch display orders:", error)
        options.value = []
    } finally {
        isLoading.value = false
    }
}

const setDisplayOrder = (i) => {
    if (currentData.value) {
        currentData.value.displayOrder = i
    }
}

// --- Lifecycle & Watchers ---

onMounted(() => {
    fetchOptions()
})

/**
 * Watch both tableName and id. If the form switches context
 * (e.g., navigating between records), the list refreshes.
 */
watch([tableName, id], () => {
    fetchOptions()
})
</script>

<template>
    <label v-if="showDisplayOrderOption" class="d-flex align-items-center">
        <div class="dropdown me-2">
            <Button
                id="dropdownMenuDisplayOrder"
                aria-expanded="true"
                class="btn-sm dropdown-toggle border"
                data-bs-toggle="dropdown"
                :disabled="isLoading"
            >
                {{ currentData?.displayOrder }}
            </Button>

            <ul aria-labelledby="dropdownMenuDisplayOrder" class="dropdown-menu">
                <li v-for="(i, k) in options" :key="k">
                    <button
                        :class="{ 'active': i === currentData?.displayOrder }"
                        class="dropdown-item"
                        type="button"
                        @click="setDisplayOrder(i)"
                    >
                        {{ i }}
                    </button>
                </li>
            </ul>
        </div>

        Display order
    </label>
</template>

<style scoped>
.dropdown-menu {
    max-height: 300px;
    overflow-y: auto;
}
</style>